/**
 * Mini indicator popover — spec §F5.9, second half.
 *
 * The §F5.9 dock's six buttons open this; it describes an indicator **without mapping it**
 * (that is its "Map this indicator" button), so a reader can compare the programme total for
 * C.2.1 while B.2.1 is painted on the map.
 *
 * ## It is no longer the primary surface (client review R7)
 *
 * > *"The floating popover is no longer the default surface (dock buttons still map
 * > indicators; popover may remain for the dock but the sidebar section is primary)."*
 *
 * The content moved to `IndicatorAnalytics`, which the sidebar mounts permanently for the
 * **mapped** indicator (`SidebarAnalytics`). This shell is what the dock still opens, for the
 * one job the sidebar section cannot do: an indicator that is *not* the mapped one.
 *
 * Nothing auto-opens it. `dockInd` starts `null` (`lib/state.ts`) and only
 * `toggleDockInd`/`setDockInd` — the dock button and the ✕ — ever write it, so a reader who
 * never touches the dock never sees this panel, and the numbers reach them in the sidebar
 * instead. It is deliberately *not* deleted: it is the only per-indicator comparison surface
 * in M3, and removing it would take a working feature away to make a layout point.
 */
import { useTranslation } from 'react-i18next';

import { Widget } from '../../components/Widget';
import { useAppStore } from '../../lib/state';
import { IndicatorAnalytics } from './IndicatorAnalytics';
import { useIndicatorMeta } from './indicatorMeta';

export function IndicatorPopover() {
  const { t } = useTranslation();
  const dockInd = useAppStore((s) => s.dockInd);
  const setDockInd = useAppStore((s) => s.setDockInd);
  const open = Boolean(dockInd);

  const { ind, color } = useIndicatorMeta(dockInd, open);

  if (!open || !ind) return null;

  return (
    <Widget
      id="indPopover"
      title={ind.name}
      collapsible={false}
      onClose={() => setDockInd(null)}
      headLead={
        <span
          className="dot"
          data-ipodot=""
          style={{ width: 10, height: 10, borderRadius: 3, background: color }}
          aria-hidden="true"
          title={t('dock.title')}
        />
      }
    >
      <div id="ipoBody">
        <IndicatorAnalytics indicatorId={dockInd} width={292} showMapButton />
      </div>
    </Widget>
  );
}

export default IndicatorPopover;
